import React, { useState, useEffect, useMemo, forwardRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import axios from "../../libs/axios";
import { useLocation } from "react-router-dom";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const CHART_FONT = "Arial, Helvetica, sans-serif";
const PUMP_COLORS = ["#0F3860", "#C8A36B", "#2A537B", "#B08C55"];
const GRID_MAJOR = "#666666";
const GRID_MINOR = "#b0b0b0";
const REF_X_AXIS = { max: 4500, major: 1000, minor: 250 };
const REF_Y_AXIS = { max: 1400, major: 200, minor: 50 };
const CM_TO_PX = 96 / 2.54;
export const PUMPSET_CHART_WIDTH_PX = Math.round(20.97 * CM_TO_PX);
export const PUMPSET_CHART_PLOT_HEIGHT_PX = Math.round(12 * CM_TO_PX);
export const PUMPSET_CHART_HEIGHT_PX = Math.round(14 * CM_TO_PX);
const CHART_WIDTH_PX = PUMPSET_CHART_WIDTH_PX;
const CHART_PADDING_PX = Math.round(0.25 * CM_TO_PX);
const CHART_INNER_WIDTH_PX = CHART_WIDTH_PX - 2 * CHART_PADDING_PX;
const CHART_PLOT_HEIGHT_PX = PUMPSET_CHART_PLOT_HEIGHT_PX;
const LEGEND_ROW_HEIGHT_PX = 18;
const LEGEND_PAD_PX = 10;
const LEGEND_GAP_PX = 6;
const LEGEND_ITEMS_PER_ROW = 4;
const LEGEND_ICON_SLOT_PX = 18;
const LEGEND_LABEL_GAP_PX = 6;

const legendItemStyle = {
  display: "grid",
  gridTemplateColumns: `${LEGEND_ICON_SLOT_PX}px 1fr`,
  columnGap: LEGEND_LABEL_GAP_PX,
  alignItems: "center",
  minWidth: 0,
  height: LEGEND_ROW_HEIGHT_PX,
};

const LegendLineIcon = ({ color }) => (
  <span
    style={{
      width: LEGEND_ICON_SLOT_PX,
      height: LEGEND_ROW_HEIGHT_PX,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "flex-start",
    }}
  >
    <span
      style={{
        width: 14,
        height: 4,
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  </span>
);

const LegendPointIcon = ({ style, color, fill }) => (
  <span
    style={{
      width: LEGEND_ICON_SLOT_PX,
      height: LEGEND_ROW_HEIGHT_PX,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "flex-start",
    }}
  >
    <LegendSymbolBox style={style} color={color} fill={fill} />
  </span>
);

const computeLegendLayout = (lineCount, pointCount) => {
  if (lineCount === 0 && pointCount === 0) {
    return { height: 0, fontSize: 10 };
  }

  const lineRows =
    lineCount > 0 ? Math.ceil(lineCount / LEGEND_ITEMS_PER_ROW) : 0;
  const pointRows =
    pointCount > 0 ? Math.ceil(pointCount / LEGEND_ITEMS_PER_ROW) : 0;
  const sectionGap = lineRows > 0 && pointRows > 0 ? LEGEND_GAP_PX : 0;
  const height =
    LEGEND_PAD_PX +
    lineRows * LEGEND_ROW_HEIGHT_PX +
    pointRows * LEGEND_ROW_HEIGHT_PX +
    sectionGap;
  const fontSize = pointCount > 8 ? 9 : 10;

  return { height, fontSize };
};

const POINT_MARKER_COLORS = [
  "#791664",
  "#607792",
  "#3F6280",
  "#8AD0BF",
  "#5A4E9E",
  "#4F6D8A",
  "#3A86A8",
  "#6FD3C1",
  "#8B5FBF",
  "#567C99",
  "#2F5F85",
  "#7EE0D6",
  "#6A3D9A",
  "#7393B3",
  "#4CC9F0",
];
const POINT_MARKER_STYLES = ["rectRot", "triangle", "circle", "rect"];
const INSTALLATION_MARKERS = POINT_MARKER_COLORS.map((color, index) => ({
  style: POINT_MARKER_STYLES[index % POINT_MARKER_STYLES.length],
  color,
}));

const markerFillColor = (marker) => marker.fill || marker.color;

const parseNum = (value) => {
  if (value === "" || value == null) return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

const isMajorTick = (value, majorStep) => {
  const rem = Math.abs(((value % majorStep) + majorStep) % majorStep);
  return rem < 1e-6 || Math.abs(rem - majorStep) < 1e-6;
};

const computeReferenceAxisScale = (values, { max: refMax, major, minor }) => {
  const valid = values.filter((v) => Number.isFinite(v));
  let max = refMax;
  if (valid.length) {
    const dataMax = Math.max(0, ...valid);
    if (dataMax > refMax) {
      max = Math.ceil(dataMax / major) * major;
    }
  }
  return { min: 0, max, minorStep: minor, majorStep: major };
};

const buildLinearScale = ({
  min,
  max,
  minorStep,
  majorStep,
  title,
  titlePadding,
}) => ({
  min,
  max,
  afterBuildTicks: (axis) => {
    const ticks = [];
    for (let v = min; v <= max + minorStep * 0.01; v += minorStep) {
      const rounded = Math.round(v * 1000) / 1000;
      ticks.push({
        value: rounded,
        major: isMajorTick(rounded, majorStep),
      });
    }
    axis.ticks = ticks;
  },
  title: {
    display: true,
    text: title,
    font: { family: CHART_FONT, size: 12, weight: "normal" },
    color: "#000",
    padding: titlePadding,
  },
  ticks: {
    autoSkip: false,
    maxRotation: 0,
    minRotation: 0,
    font: { family: CHART_FONT, size: 11 },
    color: "#000",
    callback: (value, index, ticks) =>
      ticks[index]?.major ? value : "",
  },
  grid: {
    color: (ctx) => (ctx.tick?.major ? GRID_MAJOR : GRID_MINOR),
    lineWidth: (ctx) => (ctx.tick?.major ? 1.1 : 0.75),
    drawOnChartArea: true,
    drawTicks: false,
    borderDash: [],
  },
  border: {
    color: "#000",
    width: 1,
  },
});

const LegendSymbolBox = ({ style, color, fill }) => {
  const bg = fill || color;
  if (style === "triangle") {
    return (
      <span
        style={{
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderBottom: `10px solid ${bg}`,
          display: "inline-block",
          flexShrink: 0,
        }}
      />
    );
  }
  if (style === "cross") {
    return (
      <span
        style={{
          width: 11,
          height: 11,
          backgroundColor: "#A5862A",
          color,
          fontSize: 11,
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        ✕
      </span>
    );
  }
  return (
    <span
      style={{
        width: 8,
        height: 8,
        backgroundColor: bg,
        borderRadius: style === "circle" ? "50%" : 0,
        transform: style === "rectRot" ? "rotate(45deg)" : undefined,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
};

const legendGridStyle = {
  display: "grid",
  gridTemplateColumns: `repeat(${LEGEND_ITEMS_PER_ROW}, 1fr)`,
  columnGap: 10,
  rowGap: 5,
  alignItems: "center",
  width: "100%",
};

const BottomLegend = ({ lineItems, pointItems, height, fontSize }) => (
  <div
    style={{
      height,
      minHeight: height,
      flexShrink: 0,
      padding: "6px 10px 8px",
      fontFamily: CHART_FONT,
      fontSize,
      lineHeight: `${LEGEND_ROW_HEIGHT_PX}px`,
      color: "#333",
      boxSizing: "border-box",
      borderTop: "1px solid #cccccc",
      backgroundColor: "#ffffff",
    }}
  >
    {lineItems.length > 0 && (
      <div
        style={{
          ...legendGridStyle,
          marginBottom: pointItems.length > 0 ? LEGEND_GAP_PX : 0,
        }}
      >
        {lineItems.map((item) => (
          <span
            key={item.label}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              minWidth: 0,
            }}
          >
            <span
              style={{
                width: 32,
                height: 8,
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </span>
          </span>
        ))}
      </div>
    )}
    {pointItems.length > 0 && (
      <div style={legendGridStyle}>
        {pointItems.map((item) => (
          <span key={item.label} style={legendItemStyle}>
            <LegendPointIcon
              style={item.style}
              color={item.color}
              fill={item.fill}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </span>
          </span>
        ))}
      </div>
    )}
  </div>
);

const scatterDataset = (item, index, showLabels) => {
  const x = parseNum(item.demand_flow);
  const y = parseNum(item.demand_pressure);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  const marker = INSTALLATION_MARKERS[index % INSTALLATION_MARKERS.length];
  const label = item.sprinkler_system || `Installation ${index + 1}`;

  const fill = markerFillColor(marker);
  const outline =
    marker.style === "cross" || marker.style === "star" ? 2 : 1;

  return {
    label,
    data: [{ x, y }],
    showLine: false,
    fill: false,
    pointStyle: marker.style,
    pointRadius: 3,
    pointHoverRadius: 5,
    borderColor: marker.color,
    backgroundColor: fill,
    borderWidth: outline,
    pointBorderWidth: outline,
    pointBackgroundColor: fill,
    pointBorderColor: marker.color,
    datalabels: {
      display: showLabels,
      align: "right",
      anchor: "center",
      offset: 6,
      color: "#666666",
      font: { family: CHART_FONT, size: 9, weight: "normal" },
      formatter: (value) => `${value.x} L/min @ ${value.y} kPa`,
    },
  };
};

const PumpsetChart = forwardRef(function PumpsetChart({ text, installations }, ref) {
  const { state } = useLocation();
  const [waterSupply, setWaterSupply] = useState([]);
  const [pump1, setPump1] = useState([]);
  const [pump2, setPump2] = useState([]);
  const [pump3, setPump3] = useState([]);
  const [pump4, setPump4] = useState([]);

  const lineDataset = (label, points, color) => ({
    label,
    data: points,
    fill: false,
    borderColor: color,
    backgroundColor: color,
    borderWidth: 1.5,
    tension: 0.4,
    cubicInterpolationMode: "monotone",
    pointRadius: 0,
    pointHoverRadius: 4,
    datalabels: { display: false },
  });

  const pumpPoints = (items) =>
    items
      .map((item) => ({
        x: parseNum(item.sprinkler_pump_flow),
        y: parseNum(item.discharge),
      }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .sort((a, b) => a.x - b.x);

  const { datasets, legendLines, legendPoints, legendLayout, xScale, yScale } =
    useMemo(() => {
    const allPoints = [];
    const lineDatasets = [];
    const pointDatasets = [];
    const lineLegend = [];
    const pointLegend = [];

    const pumps = [
      { label: "Pump 1", data: pump1, color: PUMP_COLORS[0] },
      { label: "Pump 2", data: pump2, color: PUMP_COLORS[1] },
      { label: "Pump 3", data: pump3, color: PUMP_COLORS[2] },
      { label: "Pump 4", data: pump4, color: PUMP_COLORS[3] },
    ];

    pumps.forEach(({ label, data, color }) => {
      const points = pumpPoints(data);
      if (points.length > 0) {
        allPoints.push(...points);
        lineDatasets.push(lineDataset(label, points, color));
        lineLegend.push({ label, color });
      }
    });

    const installationPoints =
      installations ??
      waterSupply.filter((item) => item.checkWSupply === true);

    installationPoints.forEach((item, index) => {
      const dataset = scatterDataset(item, index, text);
      if (!dataset) return;
      allPoints.push(...dataset.data);
      pointDatasets.push(dataset);
      const marker = INSTALLATION_MARKERS[index % INSTALLATION_MARKERS.length];
      pointLegend.push({
        label: dataset.label,
        color: marker.color,
        fill: markerFillColor(marker),
        style: marker.style,
      });
    });

    const xValues = allPoints.map((p) => p.x);
    const yValues = allPoints.map((p) => p.y);

    return {
      datasets: [...lineDatasets, ...pointDatasets],
      legendLines: lineLegend,
      legendPoints: pointLegend,
      legendLayout: computeLegendLayout(
        lineLegend.length,
        pointLegend.length
      ),
      xScale: computeReferenceAxisScale(xValues, REF_X_AXIS),
      yScale: computeReferenceAxisScale(yValues, REF_Y_AXIS),
    };
  }, [
    installations,
    waterSupply,
    pump1,
    pump2,
    pump3,
    pump4,
    text,
  ]);

  const options = useMemo(
    () => ({
      responsive: false,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 8, right: Math.round(1 * CM_TO_PX), bottom: 36, left: 10 },
      },
      plugins: {
        title: {
          display: true,
          text: "Pumpset Delivery Capability vs Sprinkler Demands",
          font: {
            family: CHART_FONT,
            size: 14,
            weight: "bold",
          },
          color: "#1a3a5c",
          padding: { bottom: 10 },
        },
        tooltip: {
          callbacks: {
            title: () => "",
            label: (context) =>
              `${context.parsed.x} L/min @ ${context.parsed.y} kPa`,
          },
        },
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          type: "linear",
          position: "bottom",
          ...buildLinearScale({
            ...xScale,
            title: "Flow (L/min)",
            titlePadding: { top: 8 },
          }),
        },
        y: {
          ...buildLinearScale({
            ...yScale,
            title: "Pressure (kPa)",
            titlePadding: { bottom: 8 },
          }),
        },
      },
      elements: {
        line: {
          borderWidth: 1.5,
          tension: 0.4,
        },
        point: {
          hitRadius: 8,
        },
      },
    }),
    [xScale, yScale]
  );

  const data = useMemo(() => ({ datasets }), [datasets]);

  const legendHeight = legendLayout.height;
  const totalContentHeight =
    CHART_PLOT_HEIGHT_PX + legendHeight + (legendHeight > 0 ? 4 : 0);

  const get = async () => {
    try {
      const { data } = await axios.get(`/assesment/${state.id}`);
      setPump1(data.pump1?.pump_performance || []);
      setPump2(data.pump2?.pump_performance || []);
      setPump3(data.pump3?.pump_performance || []);
      setPump4(data.pump4?.pump_performance || []);
      setWaterSupply(
        (data.water_supply?.place || []).filter(
          (item) => item.checkWSupply === true
        )
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    get();
    // eslint-disable-next-line
  }, []);

  const captureHeight = CHART_PADDING_PX * 2 + totalContentHeight;

  const fixedChartBoxStyle = {
    backgroundColor: "#ffffff",
    padding: CHART_PADDING_PX,
    width: CHART_WIDTH_PX,
    minWidth: CHART_WIDTH_PX,
    maxWidth: CHART_WIDTH_PX,
    height: captureHeight,
    minHeight: captureHeight,
    maxHeight: captureHeight,
    boxSizing: "border-box",
    flexShrink: 0,
    flexGrow: 0,
  };

  return (
    <div ref={ref} style={fixedChartBoxStyle}>
      <div
        style={{
          width: CHART_INNER_WIDTH_PX,
          height: CHART_PLOT_HEIGHT_PX,
          minWidth: CHART_INNER_WIDTH_PX,
          maxWidth: CHART_INNER_WIDTH_PX,
          minHeight: CHART_PLOT_HEIGHT_PX,
          maxHeight: CHART_PLOT_HEIGHT_PX,
          backgroundColor: "#ffffff",
          border: "1px solid #000",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        <Line
          options={options}
          data={data}
          width={CHART_INNER_WIDTH_PX}
          height={CHART_PLOT_HEIGHT_PX}
        />
      </div>
      {legendHeight > 0 && (
        <BottomLegend
          lineItems={legendLines}
          pointItems={legendPoints}
          height={legendHeight}
          fontSize={legendLayout.fontSize}
        />
      )}
    </div>
  );
});

export default PumpsetChart;
